/**
 * I18n Context
 * Manages locale selection and switching for the Kantera web app
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { SupportedLocale } from '@kantera/ui';

interface I18nContextType {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
};

interface I18nProviderProps {
  children: React.ReactNode;
  defaultLocale?: SupportedLocale;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children, defaultLocale = 'en' }) => {
  const { i18n } = useTranslation();
  const [locale, setLocaleState] = useState<SupportedLocale>(defaultLocale);

  const setLocale = useCallback(
    (newLocale: SupportedLocale): void => {
      i18n.changeLanguage(newLocale).catch(() => {
        // Silently fail if language change fails
      });
      setLocaleState(newLocale);

      // Persist locale to localStorage for future sessions
      if (typeof window !== 'undefined') {
        localStorage.setItem('kantera-locale', newLocale);
      }
    },
    [i18n],
  );

  // Restore locale from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedLocale = localStorage.getItem('kantera-locale');
      if (savedLocale && (savedLocale === 'en' || savedLocale === 'es')) {
        setLocale(savedLocale as SupportedLocale);
      }
    }
  }, [setLocale]);

  return <I18nContext.Provider value={{ locale, setLocale }}>{children}</I18nContext.Provider>;
};
